// /en/visa-matrix — EN interactive Thailand Visa Comparison Matrix
// Phase P-VISA-MATRIX (2026-06).
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { VISA_MATRIX, FIT_LABELS, MATRIX_ATTRIBUTES, type FitTag, type VisaRow } from "@/data/visa-matrix";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, Filter, CheckCircle2, XCircle, ChevronDown } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/en/visa-matrix";
export const TITLE = "Thailand Visa Comparison Matrix 2026 — DTV vs LTR vs Non-O vs Non-B | NYC";
export const DESC = "Compare 7 Thai visas side-by-side — DTV (5-yr), LTR (10-yr), Non-O Retirement/Marriage, Non-B Work. Free consultation — message us and get a same-day answer.";

// EN-localised attribute labels
const EN_ATTRS: { key: keyof VisaRow; valueKey: keyof VisaRow; label: string }[] = [
  { key: "durationTh", valueKey: "durationEn", label: "Visa validity" },
  { key: "stayPerEntryTh", valueKey: "stayPerEntryEn", label: "Stay per entry" },
  { key: "entries", valueKey: "entries", label: "Entries" },
  { key: "govFeeTh", valueKey: "govFeeEn", label: "Government fee" },
  { key: "financialTh", valueKey: "financialEn", label: "Financial proof" },
  { key: "workTh", valueKey: "workEn", label: "Work rights" },
  { key: "taxTh", valueKey: "taxEn", label: "Tax" },
  { key: "familyTh", valueKey: "familyEn", label: "Family" },
  { key: "renewalTh", valueKey: "renewalEn", label: "Renewal" },
  { key: "processingDays", valueKey: "processingDays", label: "Processing time" },
];

export const Route = createFileRoute("/en/visa-matrix")({
  head: () => {
    const url = `${SITE}${PATH}`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/en/visa-matrix", "en")}, { name: "description", content: ctaDesc(DESC, "/en/visa-matrix", "en")},
        { property: "og:title", content: ctaTitle(TITLE, "/en/visa-matrix", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/visa-matrix", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
        { name: "keywords", content: "Thailand visa comparison, DTV vs LTR, Non-O Retirement Thailand, Non-B work visa, Thailand visa matrix, which Thai visa, digital nomad visa Thailand" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/visa-matrix", "/en/visa-matrix")],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication", "@id": `${url}#tool`,
              name: "Thailand Visa Comparison Matrix",
              applicationCategory: "BusinessApplication", operatingSystem: "Any",
              url, inLanguage: "en",
              offers: { "@type": "Offer", price: "0", priceCurrency: "THB" },
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "ItemList", "@id": `${url}#list`,
              name: "Thai visa types compared",
              numberOfItems: VISA_MATRIX.length,
              itemListElement: VISA_MATRIX.map((v, i) => ({
                "@type": "ListItem", position: i + 1, name: v.nameEn, url: `${SITE}${v.detailPath.en}`,
              })),
            }),
          },
          faqPageSchema([
            { q: "Which Thailand visa is best for digital nomads?", a: "DTV (Destination Thailand Visa) — 5-year multiple entry, 180 days per stay (extendable +180 = 360/year). THB 10,000 fee, THB 500,000 bank proof. Remote work for non-Thai employers is allowed." },
            { q: "What is the difference between LTR and DTV?", a: "LTR is a 10-year BOI visa for HNWI, pensioners, senior remote pros, and HSP — requires USD 80K+/yr income, grants a Digital Work Permit, foreign-income tax exemption, and HSP gets 17% flat PIT. DTV is a 5-year MFA visa for general digital nomads with lower thresholds and no Thai-employment rights." },
            { q: "Which visa suits retirees over 50?", a: "Non-O Retirement or LTR Wealthy Pensioner. Non-O needs THB 800,000 in a Thai bank or THB 65,000/month income, renewed annually. LTR Pensioner needs USD 80K+/yr income and is valid 10 years." },
            { q: "Which Thai visa allows legal employment?", a: "LTR (auto Digital Work Permit), Non-B + Work Permit (Thai-company employees), and Non-O Marriage (separate WP). DTV allows remote work for non-Thai employers only. Tourist and ED visas strictly prohibit work." },
            { q: "How much money is required for each long-stay visa?", a: "DTV THB 500,000 · Non-O Marriage THB 400,000 or THB 40,000/month · Non-O Retirement THB 800,000 or THB 65,000/month · LTR USD 80,000+/yr income or USD 1M+ in assets (segment-dependent)." },
          ]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Visa Matrix", item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: VisaMatrixEn,
});

function VisaMatrixEn() {
  const [selectedFit, setSelectedFit] = useState<FitTag | "all">("all");
  const [workOnly, setWorkOnly] = useState(false);
  const [openMobile, setOpenMobile] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return VISA_MATRIX.filter((v) => {
      if (workOnly && !v.workAllowed) return false;
      if (selectedFit !== "all" && !v.fits.includes(selectedFit)) return false;
      return true;
    });
  }, [selectedFit, workOnly]);

  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-14 pb-10">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/en" className="hover:text-primary">Home</Link>
            <span className="mx-2">/</span><span>Visa Matrix</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Sparkles className="mr-1 inline h-3 w-3" /> Interactive tool · 7 Thai visas
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">Thailand Visa Comparison Matrix 2026</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            Pick the right Thai visa for your profile in 60 seconds — compare Tourist, DTV, LTR, Non-O Retirement, Non-O Marriage, Non-B Work, and Non-ED across validity, financial proof, work rights, tax, family, and fees.
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">Which Thai visa fits me?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Short answer: <b>Digital nomad → DTV</b> (5 yr, THB 500K), <b>HNWI / high earner / HSP → LTR</b> (10 yr, USD 80K+/yr, 17% PIT), <b>Retiree 50+ → Non-O Retirement</b> (THB 800K), <b>Married to a Thai → Non-O Marriage</b> (THB 400K + separate WP), <b>Thai-company employee → Non-B + Work Permit</b>, <b>Student / Muay Thai → Non-ED</b>, <b>Tourism ≤ 60 days → Tourist Visa</b>.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> Call +66 92 242 9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-card/50 px-4 py-5 sticky top-0 z-20 backdrop-blur">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-foreground/80">
              <Filter className="h-3.5 w-3.5" /> Filter:
            </div>
            <button
              onClick={() => setSelectedFit("all")}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${selectedFit === "all" ? "bg-primary text-primary-foreground" : "border border-border bg-background hover:border-primary"}`}
            >All</button>
            {(Object.keys(FIT_LABELS) as FitTag[]).map((tag) => (
              <button
                key={tag}
                onClick={() => setSelectedFit(tag)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${selectedFit === tag ? "bg-primary text-primary-foreground" : "border border-border bg-background hover:border-primary"}`}
              >{FIT_LABELS[tag].en}</button>
            ))}
            <label className="ml-auto inline-flex items-center gap-2 text-xs font-semibold cursor-pointer">
              <input type="checkbox" checked={workOnly} onChange={(e) => setWorkOnly(e.target.checked)} className="h-4 w-4 accent-primary" />
              Work-allowed only
            </label>
          </div>
          <div className="mt-2 text-xs text-muted-foreground">Showing {filtered.length}/{VISA_MATRIX.length} visas</div>
        </div>
      </section>

      <section className="px-4 py-8 hidden lg:block">
        <div className="mx-auto max-w-6xl overflow-x-auto">
          <table className="w-full min-w-[920px] border-collapse text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-background border-b border-border px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Attribute</th>
                {filtered.map((v) => (
                  <th key={v.slug} className="border-b border-border px-3 py-3 text-left align-top min-w-[170px]">
                    <div className="text-2xl">{v.icon}</div>
                    <div className="mt-1 text-sm font-extrabold">{v.nameEn}</div>
                    <div className="mt-0.5 text-[11px] font-normal text-muted-foreground line-clamp-2">{v.taglineEn}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {EN_ATTRS.map((attr) => (
                <tr key={attr.label} className="border-b border-border/50 hover:bg-muted/30">
                  <td className="sticky left-0 z-10 bg-background px-3 py-3 text-xs font-bold text-foreground">{attr.label}</td>
                  {filtered.map((v) => (
                    <td key={v.slug} className="px-3 py-3 align-top text-xs leading-relaxed">
                      {attr.valueKey === "entries" ? (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${v.entries === "multiple" ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200" : "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"}`}>
                          {v.entries === "multiple" ? "Multiple" : "Single"}
                        </span>
                      ) : attr.valueKey === "workEn" ? (
                        <div className="flex items-start gap-1.5">
                          {v.workAllowed ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> : <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" />}
                          <span>{v.workEn}</span>
                        </div>
                      ) : (
                        <span>{String(v[attr.valueKey])}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="bg-primary/5">
                <td className="sticky left-0 z-10 bg-primary/5 px-3 py-3 text-xs font-bold text-foreground">NYC service fee</td>
                {filtered.map((v) => (
                  <td key={v.slug} className="px-3 py-3 text-sm font-extrabold text-primary">
                    THB {v.servicePriceTHB.toLocaleString()}
                    <div className="mt-0.5 text-[10px] font-normal text-muted-foreground">+ government fee</div>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="sticky left-0 z-10 bg-background px-3 py-3"></td>
                {filtered.map((v) => (
                  <td key={v.slug} className="px-3 py-3">
                    <Link to={v.detailPath.en as never} className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground hover:opacity-90">
                      View details <ArrowRight className="h-3 w-3" />
                    </Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="px-4 py-8 lg:hidden">
        <div className="mx-auto max-w-2xl space-y-3">
          {filtered.map((v) => (
            <div key={v.slug} className="rounded-2xl border border-border bg-card">
              <button
                onClick={() => setOpenMobile(openMobile === v.slug ? null : v.slug)}
                className="flex w-full items-start gap-3 p-4 text-left"
              >
                <span className="text-2xl">{v.icon}</span>
                <div className="flex-1">
                  <div className="font-extrabold text-sm">{v.nameEn}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{v.taglineEn}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold">{v.durationEn}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${v.workAllowed ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200" : "bg-rose-100 text-rose-900 dark:bg-rose-900/40 dark:text-rose-200"}`}>
                      {v.workAllowed ? "Work OK" : "No work"}
                    </span>
                    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold text-primary">THB {v.servicePriceTHB.toLocaleString()}</span>
                  </div>
                </div>
                <ChevronDown className={`h-4 w-4 shrink-0 transition ${openMobile === v.slug ? "rotate-180" : ""}`} />
              </button>
              {openMobile === v.slug && (
                <div className="border-t border-border px-4 py-3 text-xs space-y-2">
                  <p className="leading-relaxed text-muted-foreground">{v.quickAnswerEn}</p>
                  <dl className="mt-3 grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2">
                    {EN_ATTRS.filter((a) => a.valueKey !== "entries").map((attr) => (
                      <div key={attr.label}>
                        <dt className="font-bold text-foreground/80">{attr.label}</dt>
                        <dd className="text-muted-foreground">{String(v[attr.valueKey])}</dd>
                      </div>
                    ))}
                  </dl>
                  <Link to={v.detailPath.en as never} className="mt-3 inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground">
                    Full details <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">Visa quick-answers</h2>
          <p className="mt-2 text-sm text-muted-foreground">Short ~60-word summaries optimised for ChatGPT, Gemini, and Perplexity answer panels.</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {VISA_MATRIX.map((v) => (
              <div key={v.slug} className="rounded-2xl border border-border bg-card p-5 aeo-speakable" data-speakable="true">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{v.icon}</span>
                  <h3 className="text-sm font-extrabold">{v.nameEn}</h3>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{v.quickAnswerEn}</p>
                <Link to={v.detailPath.en as never} className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline">
                  Read more <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-10">
        <div className="mx-auto max-w-5xl rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 p-6 sm:p-8">
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="inline-block rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">AI Wizard · 60 seconds</span>
              <h2 className="mt-2 text-xl font-extrabold sm:text-2xl">Not sure which visa fits? Answer 5 questions.</h2>
              <p className="mt-1 text-sm text-muted-foreground">The wizard scores all 7 long-stay Thai visas with reasons and fees.</p>
            </div>
            <Link to="/en/visa-wizard" className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              Start the Wizard <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">

        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">Related services</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "DTV (Digital Nomad)", d: "5-year visa across 9 categories", to: "/en/dtv" as const },
              { t: "LTR (BOI 10-year)", d: "HSP 17% PIT · Pensioner · HNWI", to: "/en/ltr" as const },
              { t: "TM30 / 90-day Compliance", d: "Reporting · re-entry · address", to: "/en/visa-compliance" as const },
              { t: "Work Permit", d: "All nationalities · Thai employers", to: "/en/work-permit" as const },
              { t: "Company Setup", d: "Thai Limited · BOI · Branch · Rep", to: "/en/company-setup" as const },
              { t: "Apostille / MFA", d: "Document legalisation worldwide", to: "/en/mfa-legalisation" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary transition">
                <div className="font-bold text-sm">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    </main>
  );
}
